/*
 * Biotane product accordion stabilizer
 * Keeps Concept product/FAQ accordions usable and smoothly animated even when
 * the theme's customized built-in <details is="accordion-details"> animation
 * does not initialize correctly.
 */
(() => {
  'use strict';

  if (window.__biotaneProductAccordionsV1) return;
  window.__biotaneProductAccordionsV1 = true;

  const SELECTOR = 'details[is="accordion-details"]';
  const SUMMARY_SELECTOR = 'details[is="accordion-details"] > summary';
  const animations = new WeakMap();

  const reducedMotion = () =>
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function isManaged(details) {
    return details instanceof HTMLDetailsElement &&
      details.matches(SELECTOR) &&
      Boolean(details.closest('body.template-product'));
  }

  function getParts(details) {
    const summary = details.querySelector(':scope > summary');
    const content = summary ? summary.nextElementSibling : null;
    return { summary, content };
  }

  function cancelAnimation(details) {
    const active = animations.get(details);
    if (active) {
      try { active.cancel(); } catch (_) {}
      animations.delete(details);
    }
  }

  function cleanup(details, content) {
    details.style.removeProperty('height');
    details.style.removeProperty('overflow');
    if (content) {
      content.style.removeProperty('opacity');
      content.style.removeProperty('transform');
    }
  }

  function setExpanded(details, expanded) {
    details.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  }

  function openInstant(details) {
    cancelAnimation(details);
    details.setAttribute('open', '');
    setExpanded(details, true);
    cleanup(details, getParts(details).content);
  }

  function closeInstant(details) {
    cancelAnimation(details);
    details.removeAttribute('open');
    setExpanded(details, false);
    cleanup(details, getParts(details).content);
  }

  function openAnimated(details) {
    const { summary, content } = getParts(details);
    if (!summary || !content || reducedMotion() || typeof details.animate !== 'function') {
      openInstant(details);
      return Promise.resolve();
    }

    cancelAnimation(details);

    details.setAttribute('open', '');
    setExpanded(details, true);
    details.style.overflow = 'hidden';

    const startHeight = summary.getBoundingClientRect().height;
    const endHeight = details.scrollHeight;

    content.style.opacity = '0';
    content.style.transform = 'translateY(8px)';

    const animation = details.animate(
      [{ height: `${startHeight}px` }, { height: `${endHeight}px` }],
      { duration: 280, easing: 'cubic-bezier(.22, 1, .36, 1)' }
    );
    animations.set(details, animation);

    const contentAnimation = content.animate(
      [
        { opacity: 0, transform: 'translateY(8px)' },
        { opacity: 1, transform: 'translateY(0)' }
      ],
      { duration: 180, delay: 80, easing: 'ease-out', fill: 'both' }
    );

    return animation.finished
      .catch(() => {})
      .then(() => contentAnimation.finished.catch(() => {}))
      .finally(() => {
        // Ignore a stale open finalizer if a newer animation took over.
        if (animations.get(details) !== animation) return;
        animations.delete(details);
        if (details.hasAttribute('open')) cleanup(details, content);
      });
  }

  function closeAnimated(details) {
    const { summary, content } = getParts(details);
    if (!summary || !content || reducedMotion() || typeof details.animate !== 'function') {
      closeInstant(details);
      return Promise.resolve();
    }

    cancelAnimation(details);

    setExpanded(details, false);
    details.style.overflow = 'hidden';

    const startHeight = details.getBoundingClientRect().height;
    const endHeight = summary.getBoundingClientRect().height;

    const contentAnimation = content.animate(
      [{ opacity: 1 }, { opacity: 0 }],
      { duration: 120, easing: 'ease-out', fill: 'both' }
    );

    const animation = details.animate(
      [{ height: `${startHeight}px` }, { height: `${endHeight}px` }],
      { duration: 240, easing: 'cubic-bezier(.4, 0, .2, 1)' }
    );
    animations.set(details, animation);

    return Promise.all([
      animation.finished.catch(() => {}),
      contentAnimation.finished.catch(() => {})
    ]).finally(() => {
      // Ignore a stale close finalizer if a newer open/close animation took over.
      if (animations.get(details) !== animation) return;
      animations.delete(details);
      details.removeAttribute('open');
      cleanup(details, content);
    });
  }

  function closeSiblings(details) {
    const group = details.closest('accordions-details');
    if (!group) return;

    group.querySelectorAll(':scope details[is="accordion-details"][open]').forEach((item) => {
      if (item !== details && isManaged(item)) closeAnimated(item);
    });
  }

  function handleClick(event) {
    const summary = event.target.closest(SUMMARY_SELECTOR);
    if (!summary) return;

    const details = summary.parentElement;
    if (!isManaged(details)) return;

    // Take ownership before Concept's AccordionDetails click handler runs.
    // This avoids the broken preventDefault + Motion dependency path.
    event.preventDefault();
    event.stopPropagation();

    if (details.hasAttribute('open')) {
      closeAnimated(details);
    } else {
      closeSiblings(details);
      openAnimated(details);
    }
  }

  function syncState(root = document) {
    root.querySelectorAll?.(SELECTOR).forEach((details) => {
      setExpanded(details, details.hasAttribute('open'));
      cleanup(details, getParts(details).content);
    });
  }

  document.addEventListener('click', handleClick, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => syncState(), { once: true });
  } else {
    syncState();
  }

  // Shopify theme editor and some apps can re-render product sections dynamically.
  document.addEventListener('shopify:section:load', (event) => syncState(event.target));
})();
